// CRB14: explicit V2 state. V1 builders keep their original serialization.
import type { CustomReportCatalogDto, CustomReportResultDto } from "./api.ts";
import {
  buildSpec,
  stateFromSpec,
  measureAlias,
  chartSlot,
  type BuilderState,
  type CatalogDataset,
  type MeasureChoice,
  type FilterDraft,
  type SpecResult,
} from "./customReportModel.ts";
export type CommonKey =
  | "location_id"
  | "business_date"
  | "month"
  | "iso_week"
  | "day_of_week"
  | "product_id"
  | "employee_id"
  | "drink_id"
  | "statement_month"
  | "calendar_paid_date";
export type Arithmetic =
  | { op: "measure"; source: string; measure: string }
  | { op: "constant"; value: string }
  | { op: "add" | "subtract" | "multiply" | "divide"; left: Arithmetic; right: Arithmetic };
export interface CompositeSourceState {
  id: string;
  dataset: string;
  measures: Array<MeasureChoice & { as?: string }>;
  filters: FilterDraft[];
  nextFilterId: number;
  filterAggregations?: Record<number, string>;
}
export interface CalculationState {
  as: string;
  label: string;
  expression: Arithmetic;
  percent?: boolean;
}
export interface CompositeState {
  sources: CompositeSourceState[];
  calculations: CalculationState[];
  chart?: Record<string, unknown>;
  tileMeasure?: string;
  groupLabels?: Record<string, string>;
  extraSort?: Array<{ by: string; dir: "asc" | "desc" }>;
}
export const COMMON_LABELS: Record<CommonKey, string> = {
  location_id: "Location",
  business_date: "Business date",
  month: "Month",
  iso_week: "Week",
  day_of_week: "Day of week",
  product_id: "Item",
  employee_id: "Employee",
  drink_id: "Drink",
  statement_month: "Statement month",
  calendar_paid_date: "Paid date",
};
export const BASIS_LABELS: Record<string, string> = {
  business_day: "Business date",
  calendar_paid_day: "Calendar paid date",
  statement_month: "Statement month",
  snapshot: "As of run",
};
export function firstDataset(raw: unknown, datasets: CatalogDataset[]): CatalogDataset | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const spec = raw as {
    dataset?: unknown;
    sources?: Array<{ dataset?: unknown }>;
    specVersion?: unknown;
  };
  return datasets.find(
    (d) => d.id === (spec.specVersion === 2 ? spec.sources?.[0]?.dataset : spec.dataset),
  );
}
export function sourceAlias(measure: MeasureChoice & { as?: string }): string {
  return measure.as ?? measureAlias(measure);
}
export function sourceState(state: BuilderState, source: CompositeSourceState): BuilderState {
  const next = {
    ...state,
    dataset: source.dataset,
    dimensions: [],
    measures: source.measures,
    filters: source.filters,
    nextFilterId: source.nextFilterId,
    sortBy: "",
    compare: "" as const,
    chart: null,
  };
  delete next.composite;
  delete next.tile;
  return next;
}
export function addCompositeSource(
  state: BuilderState,
  added: CatalogDataset,
  datasets: CatalogDataset[] = [],
): BuilderState {
  const existing = state.composite?.sources ?? [
    {
      id: "s1",
      dataset: state.dataset,
      measures: state.measures,
      filters: state.filters,
      nextFilterId: state.nextFilterId,
    },
  ];
  if (existing.some((s) => s.dataset === added.id)) return state;
  let index = 1;
  while (existing.some((s) => s.id === `s${index}`)) index++;
  const initial: CompositeState = state.composite ?? {
    calculations: [],
    sources: existing,
    groupLabels: Object.fromEntries(
      state.dimensions.map((k) => [
        k,
        datasets.find((d) => d.id === state.dataset)?.fields.find((f) => f.name === k)?.label ??
          "Previous group",
      ]),
    ),
  };
  if (state.composite === undefined) {
    const ds = datasets.find((d) => d.id === state.dataset);
    if (ds && state.chart !== null) {
      const slot = chartSlot(state, ds);
      if (slot.ok) initial.chart = { ...slot.chart, y: slot.chart.y.map((y) => `s1_${y}`) };
    }
    if (state.tile !== undefined && state.measures.length === 1)
      initial.tileMeasure = `s1_${sourceAlias(state.measures[0]!)}`;
  }
  return {
    ...state,
    composite: {
      ...initial,
      sources: [
        ...existing,
        { id: `s${index}`, dataset: added.id, measures: [], filters: [], nextFilterId: 1 },
      ],
    },
  };
}
export function compositeProblem(state: BuilderState, datasets: CatalogDataset[]): string {
  const comp = state.composite;
  if (comp === undefined) return "";
  if (comp.sources.length < 2) return "Choose at least two sources.";
  for (const source of comp.sources) {
    const ds = datasets.find((d) => d.id === source.dataset);
    if (ds?.composite === undefined) return `${ds?.label ?? "A selected source"} is not available.`;
  }
  for (const key of state.dimensions) {
    const missing = comp.sources.filter(
      (s) =>
        !datasets.find((d) => d.id === s.dataset)?.composite?.dimensions.some((d) => d.key === key),
    );
    if (missing.length)
      return `${COMMON_LABELS[key as CommonKey] ?? comp.groupLabels?.[key] ?? "Previous group"} unavailable: ${missing.map((s) => datasets.find((d) => d.id === s.dataset)?.label ?? "A selected source").join(", ")}.`;
  }
  const bases = new Set(
    comp.sources.map((s) => datasets.find((d) => d.id === s.dataset)?.composite?.dateBasis),
  );
  if (state.compare !== "" && (bases.has("snapshot") || bases.size > 1))
    return "Comparison unavailable for these source dates.";
  if (
    bases.size > 1 &&
    state.dimensions.some((k) =>
      [
        "business_date",
        "month",
        "iso_week",
        "day_of_week",
        "statement_month",
        "calendar_paid_date",
      ].includes(k),
    )
  )
    return "These sources use different date bases. Remove the date grouping.";
  const aliases = new Set(
    comp.sources.flatMap((s) => s.measures.map((m) => `${s.id}_${sourceAlias(m)}`)),
  );
  let nodes = 0;
  const expressionProblem = (expr: Arithmetic, depth = 1): string => {
    if (++nodes > 32 || depth > 8) return "The calculation is too large.";
    if (expr.op === "measure")
      return aliases.has(`${expr.source}_${expr.measure}`)
        ? ""
        : "A calculation names an unavailable source measure.";
    if (expr.op === "constant")
      return /^-?(?:0|[1-9][0-9]{0,8})(?:\.[0-9]{1,6})?$/.test(expr.value)
        ? ""
        : "Enter a valid calculation number.";
    return expressionProblem(expr.left, depth + 1) || expressionProblem(expr.right, depth + 1);
  };
  for (const c of comp.calculations) {
    if (!c.label.trim()) return "Name each calculation.";
    const error = expressionProblem(c.expression);
    if (error) return `${c.label}: ${error}`;
  }
  const results = new Set([...aliases, ...comp.calculations.map((c) => c.as)]);
  if (state.sortBy !== "" && !results.has(state.sortBy) && !state.dimensions.includes(state.sortBy))
    return "Choose an available sort result.";
  if (comp.extraSort?.some((s) => !results.has(s.by) && !state.dimensions.includes(s.by)))
    return "Remove an unavailable sort result.";
  if (comp.chart !== undefined) {
    const chart = comp.chart,
      y = chart["y"];
    if (!Array.isArray(y) || !y.length || y.some((v) => typeof v !== "string" || !results.has(v)))
      return "Choose available chart measures.";
    if (chart["type"] === "number" && (state.dimensions.length || y.length !== 1))
      return "A number needs one measure and no groups.";
    if (
      chart["type"] !== "number" &&
      (!state.dimensions.includes(String(chart["x"])) ||
        (chart["series"] != null && !state.dimensions.includes(String(chart["series"]))))
    )
      return "Choose an available chart group.";
  }
  if (state.tile !== undefined && (!comp.tileMeasure || !results.has(comp.tileMeasure)))
    return "Choose an available tile measure.";
  return "";
}
export function buildComposite(state: BuilderState, datasets: CatalogDataset[]): SpecResult {
  const comp = state.composite;
  if (comp === undefined) return { ok: false, message: "Choose report sources." };
  const problem = compositeProblem(state, datasets);
  if (problem) return { ok: false, message: problem };
  if (comp.sources.some(source => source.measures.length > 8))
    return { ok: false, message: "Choose up to eight measures per source." };
  const sources = [];
  for (const source of comp.sources) {
    const ds = datasets.find((d) => d.id === source.dataset);
    if (ds === undefined) return { ok: false, message: "A selected source is not available." };
    if (!source.measures.length) return { ok: false, message: `Choose a measure for ${ds.label}.` };
    const built = buildSpec(sourceState(state, source), ds);
    if (!built.ok) return built;
    sources.push({
      id: source.id,
      dataset: source.dataset,
      measures: source.measures.map((m) => ({ field: m.field, agg: m.agg, as: sourceAlias(m) })),
      filters: (built.spec["filters"] as Array<Record<string, unknown>>).map((filter, index) =>
        source.filterAggregations?.[source.filters[index]?.id ?? -1] === undefined
          ? filter
          : { ...filter, agg: source.filterAggregations[source.filters[index]!.id] },
      ),
    });
  }
  const base = buildSpec(
    { ...sourceState(state, comp.sources[0]!), dimensions: [] },
    datasets.find((d) => d.id === comp.sources[0]?.dataset)!,
  );
  if (!base.ok) return base;
  const spec = {
    ...base.spec,
    specVersion: 2,
    sources,
    dimensions: state.dimensions,
    calculations: comp.calculations,
    sort: [
      ...(state.sortBy === "" ? [] : [{ by: state.sortBy, dir: state.sortDir }]),
      ...(comp.extraSort ?? []),
    ],
  } as Record<string, unknown>;
  delete spec["dataset"];
  delete spec["measures"];
  delete spec["filters"];
  if (state.compare !== "") spec["compare"] = { kind: state.compare };
  const output: Record<string, unknown> = { view: "table", totals: state.totals !== false };
  if (comp.chart !== undefined) output["chart"] = comp.chart;
  if (state.tile !== undefined) {
    if (state.dimensions.length || !comp.tileMeasure)
      return { ok: false, message: "Choose one result measure for the tile." };
    if (!state.tile.label.trim() || state.tile.label.length > 120)
      return { ok: false, message: "Name the tile." };
    output["tile"] = { measure: comp.tileMeasure, label: state.tile.label.trim() };
  }
  spec["output"] = output;
  return { ok: true, spec };
}
export function compositeFromSpec(
  raw: unknown,
  datasets: CatalogDataset[],
): BuilderState | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const spec = raw as Record<string, unknown>;
  if (
    spec["specVersion"] !== 2 ||
    !Array.isArray(spec["sources"]) ||
    spec["sources"].length < 2
  )
    return undefined;
  const sources: CompositeSourceState[] = [];
  let base: BuilderState | undefined;
  for (const value of spec["sources"]) {
    if (typeof value !== "object" || value === null) return undefined;
    const source = value as Record<string, unknown>;
    const ds = datasets.find((d) => d.id === source["dataset"]);
    if (
      ds?.composite === undefined ||
      typeof source["id"] !== "string" ||
      !Array.isArray(source["measures"])
    )
      return undefined;
    const measures = source["measures"] as Array<Record<string, unknown>>;
    const parsed = stateFromSpec(
      {
        ...spec,
        specVersion: 1,
        dataset: ds.id,
        dimensions: [],
        measures: measures.map((m) => ({
          ...m,
          as: measureAlias({ field: String(m["field"]), agg: String(m["agg"]) }),
        })),
        filters: source["filters"],
        sort: [],
        output: {
          view: "table",
          totals: (spec["output"] as { totals?: boolean } | undefined)?.totals,
        },
      },
      ds,
    );
    if (parsed === undefined) return undefined;
    base ??= parsed;
    sources.push({
      id: source["id"],
      dataset: ds.id,
      measures: parsed.measures.map((m, i) => ({ ...m, as: String(measures[i]?.["as"]) })),
      filters: parsed.filters,
      nextFilterId: parsed.nextFilterId,
      filterAggregations: Object.fromEntries(
        (source["filters"] as Array<Record<string, unknown>>).flatMap((filter, index) =>
          typeof filter["agg"] === "string" ? [[parsed.filters[index]!.id, filter["agg"]]] : [],
        ),
      ),
    });
  }
  if (
    base === undefined ||
    !Array.isArray(spec["dimensions"]) ||
    !spec["dimensions"].every((d) => typeof d === "string" && d in COMMON_LABELS) ||
    !Array.isArray(spec["calculations"])
  )
    return undefined;
  let expressionNodes = 0;
  const validExpression = (expressionRaw: unknown, depth = 1): boolean => {
    if (++expressionNodes > 32 || depth > 8 || typeof expressionRaw !== "object" || expressionRaw === null)
      return false;
    const expr = expressionRaw as Record<string, unknown>;
    if (expr["op"] === "measure")
      return typeof expr["source"] === "string" && typeof expr["measure"] === "string";
    if (expr["op"] === "constant") return typeof expr["value"] === "string";
    return (
      ["add", "subtract", "multiply", "divide"].includes(String(expr["op"])) &&
      validExpression(expr["left"], depth + 1) &&
      validExpression(expr["right"], depth + 1)
    );
  };
  if (
    spec["calculations"].length > 4 ||
    !spec["calculations"].every(
      (c) =>
        typeof c === "object" &&
        c !== null &&
        typeof (c as Record<string, unknown>)["label"] === "string" &&
        typeof (c as Record<string, unknown>)["as"] === "string" &&
        validExpression((c as Record<string, unknown>)["expression"]),
    )
  )
    return undefined;
  const output = spec["output"] as
    { chart?: Record<string, unknown>; tile?: { label: string; measure: string } } | undefined;
  base.composite = {
    sources,
    calculations: structuredClone(spec["calculations"]) as CalculationState[],
    extraSort: structuredClone(
      (spec["sort"] as Array<{ by: string; dir: "asc" | "desc" }> | undefined)?.slice(1) ?? [],
    ),
    ...(output?.chart === undefined ? {} : { chart: structuredClone(output.chart) }),
    ...(output?.tile === undefined ? {} : { tileMeasure: output.tile.measure }),
  };
  base.dimensions = [...spec["dimensions"]] as string[];
  base.sortBy = (spec["sort"] as Array<{ by: string }> | undefined)?.[0]?.by ?? "";
  base.sortDir = (spec["sort"] as Array<{ dir: "asc" | "desc" }> | undefined)?.[0]?.dir ?? "desc";
  if (output?.tile !== undefined) base.tile = { label: output.tile.label };
  return base;
}
export function resultChoices(
  state: BuilderState,
  datasets: CatalogDataset[],
): Array<{ value: string; label: string }> {
  return [
    ...(state.composite?.sources ?? []).flatMap((s) =>
      s.measures.map((m) => ({
        value: `${s.id}_${sourceAlias(m)}`,
        label: `${datasets.find((d) => d.id === s.dataset)?.label ?? "Unknown source"} · ${datasets.find((d) => d.id === s.dataset)?.fields.find((f) => f.name === m.field)?.label ?? "Unknown measure"}`,
      })),
    ),
    ...(state.composite?.calculations ?? []).map((c) => ({ value: c.as, label: c.label })),
  ];
}
export type CompositeCatalog = CustomReportCatalogDto;

/** Presentation labels never replace canonical join identities in the saved spec. */
export function compositePresentation(result: CustomReportResultDto): CustomReportResultDto {
  if (result.meta.sources === undefined) return result;
  return {
    ...result,
    rows: result.rows.map((row, r) =>
      row.map((value, c) =>
        result.columns[c]?.kind === "dimension"
          ? (result.dimensionLabels?.[r]?.[c] ??
            (["location_id", "product_id", "employee_id", "drink_id"].includes(
              result.columns[c]?.key ?? "",
            )
              ? null
              : value))
          : value,
      ),
    ),
  };
}
